import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import Head from "next/head";
import Script from "next/script";
import { Formsparkform } from "../../../../studio/sanity.types";
import styles from "./FormSparkForm.module.scss";
import { EffektButton, EffektButtonVariant } from "../../../shared/components/EffektButton/EffektButton";
import { RadioButton } from "../../../shared/components/RadioButton/RadioButton";
import { EffektCheckbox } from "../../../shared/components/EffektCheckbox/EffektCheckbox";
import { redirectUrlFor, wasSubmitted } from "./formsparkRedirect";

// Type definitions for form state
type FormState = Record<string, string | boolean>;

// Type guard to check if a field has options
const hasOptions = (field: any): field is {
  options: Array<{
    value: string;
    label: string;
  }>;
} => {
  return field.options && Array.isArray(field.options);
};
export const FormsparkForm: React.FC<{
  formData: Formsparkform;
}> = t0 => {
  const $ = _c(30);
  const {
    formData
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [formState, setFormState] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== formData.fields) {
    t2 = () => {
      const initialState = {};
      formData.fields?.forEach(field => {
        if (field.field_type === "checkbox") {
          initialState[field.field_name || ""] = false;
        } else {
          initialState[field.field_name || ""] = "";
        }
      });
      setFormState(initialState);
    };
    t3 = [formData.fields];
    $[1] = formData.fields;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  React.useEffect(t2, t3);
  const [redirectUrl, setRedirectUrl] = useState();
  const [submitted, setSubmitted] = useState(false);
  let t4;
  let t5;
  if ($[4] !== formData.form_id) {
    t4 = () => {
      const formId = formData.form_id;
      if (!formId) {
        return;
      }
      setSubmitted(wasSubmitted(window.location.href, formId));
      setRedirectUrl(redirectUrlFor(window.location.href, formId));
    };
    t5 = [formData.form_id];
    $[4] = formData.form_id;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (fieldName, value) => {
      setFormState(prev => ({
        ...prev,
        [fieldName]: value
      }));
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleInputChange = t6;
  let t7;
  if ($[8] !== formState) {
    t7 = field_0 => {
      const fieldName_0 = field_0.field_name || "";
      const validation = field_0.validation || {};
      const commonProps = {
        name: fieldName_0,
        id: fieldName_0,
        required: validation.required
      };
      if (validation.min_length) {
        commonProps.minLength = validation.min_length;
      }
      if (validation.max_length) {
        commonProps.maxLength = validation.max_length;
      }
      if (validation.pattern) {
        commonProps.pattern = validation.pattern;
        if (validation.pattern_message) {
          commonProps.title = validation.pattern_message;
        }
      }
      if (field_0.autocomplete) {
        commonProps.autoComplete = field_0.autocomplete;
      }
      switch (field_0.field_type) {
        case "textarea":
          {
            return <textarea {...commonProps} value={formState[fieldName_0] as string || ""} onChange={e_2 => handleInputChange(fieldName_0, e_2.target.value)} placeholder={field_0.placeholder} rows={field_0.rows || 4} />;
          }
        case "select":
          {
            return <select {...commonProps} value={formState[fieldName_0] as string || ""} onChange={e_1 => handleInputChange(fieldName_0, e_1.target.value)}><option value="">Choose an option</option>{hasOptions(field_0) && field_0.options.map(_temp)}</select>;
          }
        case "radio":
          {
            return <div role="radiogroup" aria-labelledby={`${fieldName_0}-label`} className={styles.radioGroup}>{hasOptions(field_0) && field_0.options.map((option, index) => <div key={index}><RadioButton name={fieldName_0} selected={formState[fieldName_0] === option.value} onSelect={() => handleInputChange(fieldName_0, option.value)} title={option.label} data_cy={`${fieldName_0}-${index}`} required={index === 0 && (formState[fieldName_0] === "" || formState[fieldName_0] === undefined)} /></div>)}</div>;
          }
        case "checkbox":
          {
            return <div><EffektCheckbox required={commonProps.required} checked={!!formState[fieldName_0]} onChange={checked => handleInputChange(fieldName_0, checked)}>{field_0.field_label}</EffektCheckbox></div>;
          }
        case "number":
          {
            const numberProps = {
              ...commonProps
            };
            if (validation.min_value !== undefined) {
              numberProps.min = validation.min_value;
            }
            if (validation.max_value !== undefined) {
              numberProps.max = validation.max_value;
            }
            return <input {...numberProps} type="number" value={formState[fieldName_0] as string || ""} onChange={e_0 => handleInputChange(fieldName_0, e_0.target.value)} placeholder={field_0.placeholder} />;
          }
        default:
          {
            return <input {...commonProps} type={field_0.field_type || "text"} value={formState[fieldName_0] as string || ""} onChange={e => handleInputChange(fieldName_0, e.target.value)} placeholder={field_0.placeholder} />;
          }
      }
    };
    $[8] = formState;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const renderField = t7;
  if (!formData || !formData.fields) {
    let t8;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div>No form data provided</div>;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    return t8;
  }
  let t8;
  if ($[11] !== formData.turnstile_site_key) {
    t8 = formData.turnstile_site_key && <><Head><link rel="preconnect" href="https://challenges.cloudflare.com" /></Head><Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="afterInteractive" /></>;
    $[11] = formData.turnstile_site_key;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = `form-${formData.form_id}`;
  let t10;
  if ($[13] !== formData.fields || $[14] !== formData.form_id || $[15] !== formData.honeypot_field || $[16] !== formData.submit_button_text || $[17] !== formData.submit_target || $[18] !== formData.success_message || $[19] !== formData.turnstile_site_key || $[20] !== redirectUrl || $[21] !== renderField || $[22] !== submitted) {
    t10 = submitted && formData.success_message ? <p className={styles.success} role="status">{formData.success_message}</p> : <form action={`https://submit-form.com/${formData.form_id}`} method="POST" target={formData.submit_target || "_self"} className={styles.form}>{redirectUrl && <input type="hidden" name="_redirect" value={redirectUrl} />}{formData.fields.map((field_1, index_1) => {
        const fieldName_1 = field_1.field_name || "";
        return <div key={index_1} className={styles.fieldWrapper}>{field_1.field_type !== "checkbox" && <label htmlFor={fieldName_1} id={`${fieldName_1}-label`}>{field_1.field_label}</label>}{renderField(field_1)}</div>;
      })}{formData.turnstile_site_key && <div className="cf-turnstile" data-sitekey={formData.turnstile_site_key} />}{formData.honeypot_field && <input type="text" name="_gotcha" style={{
        display: "none"
      }} tabIndex={-1} autoComplete="off" />}<EffektButton variant={EffektButtonVariant.PRIMARY} type="submit">{formData.submit_button_text || "Submit"}</EffektButton></form>;
    $[13] = formData.fields;
    $[14] = formData.form_id;
    $[15] = formData.honeypot_field;
    $[16] = formData.submit_button_text;
    $[17] = formData.submit_target;
    $[18] = formData.success_message;
    $[19] = formData.turnstile_site_key;
    $[20] = redirectUrl;
    $[21] = renderField;
    $[22] = submitted;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = <div className={styles.wrapper} id={t9}>{t10}</div>;
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11 || $[28] !== t8) {
    t12 = <>{t8}{t11}</>;
    $[27] = t11;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
};
function _temp(option_0, index_0) {
  return <option key={index_0} value={option_0.value}>{option_0.label}</option>;
}